/**
 * Term sheet generator (`/admin/termsheets/:dealId`) store. Inputs load from
 * and save to the HubSpot deal via `/api/admin/termsheets/*`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { cfApi } from "~/store/server/cf/client"
import type {
  InputIssue,
  QuoteResponse,
  RtlLoanInput,
} from "~/store/admin/capital-engine"

export type TermSheetValue = string | number | boolean | null
export type TermSheetValues = Record<string, TermSheetValue>

export type FieldKind =
  | "text"
  | "number"
  | "currency"
  | "percent"
  | "date"
  | "enum"
  | "bool"

export interface TermSheetField {
  key: string
  section: string
  label: string
  kind: FieldKind
  options?: string[]
  group?: string
  priced: boolean
  readOnly?: boolean
}

export interface TermSheetSource {
  object: "deal" | "contact"
  property: string
}

/** An issued term sheet: the recorded inputs plus the engine output. */
export interface IssuedTermSheetSummary {
  id: number
  dealId: string
  family: string
  version: string
  productId: string | null
  reason: string
  issuedAt: string
  issuedBy: number | null
}

export interface IssuedTermSheet extends IssuedTermSheetSummary {
  inputs: TermSheetValues
  outputs: {
    loan?: RtlLoanInput
    products?: QuoteResponse["products"]
    inputIssues?: QuoteResponse["inputIssues"]
  }
}

export interface HubSpotSync {
  /** Changed in HubSpot since the last sync; not edited here. */
  incoming: string[]
  /** Changed both here and in HubSpot, to different values. */
  conflicts: string[]
  /** Edited here, not yet in HubSpot. */
  unpushed: string[]
}

export interface TermSheet {
  dealId: string
  dealName: string | null
  dealStage: string | null
  sections: Array<{ id: string; title: string }>
  fields: TermSheetField[]
  /** Null until a policy is picked for the deal. */
  draft: { family: string; version: string; updatedAt: string } | null
  values: TermSheetValues
  hubspotValues: TermSheetValues
  sync: HubSpotSync
  /** Fields with no HubSpot property: only ever stored locally. */
  localOnly: string[]
  sources: Record<string, TermSheetSource | null>
  issued: { count: number; latest: IssuedTermSheet | null }
}

export interface PushedTermSheet extends TermSheet {
  rejected: Array<{ object: string; property: string; reason: string }>
}

/** An engine input issue, pinned to the term sheet field it's about. */
export type TermSheetInputIssue = InputIssue & { fieldKey?: string }

export type PricedTermSheet = Omit<QuoteResponse, "inputIssues"> & {
  loan: RtlLoanInput
  inputIssues: TermSheetInputIssue[]
}

export type TermSheetListScope = "active" | "quotes" | "preclose"

export interface TermSheetListRow {
  id: string
  street: string | null
  city: string | null
  state: string | null
  zip: string | null
  loanNumber: string | null
  dealstage: { value: string; label: string } | null
  closedate: string | null
  ae: { id: string; name: string } | null
  /** When terms were last issued; null when never. */
  lastIssuedAt: string | null
}

export interface TermSheetListPage {
  results: TermSheetListRow[]
  nextCursor: string | null
  total: number | null
}

const BASE = "/api/admin/termsheets"

export const termSheetKeys = {
  all: ["cf", "admin", "termsheets"] as const,
  deal: (dealId: string) => [...termSheetKeys.all, dealId] as const,
  history: (dealId: string) =>
    [...termSheetKeys.all, dealId, "history"] as const,
  list: (scope: string, aeId: string, cursor: string) =>
    [...termSheetKeys.all, "list", scope, aeId, cursor] as const,
}

export function useTermSheetList(params: {
  scope: TermSheetListScope
  aeId?: string
  cursor?: string
}) {
  return useQuery<TermSheetListPage>({
    queryKey: termSheetKeys.list(
      params.scope,
      params.aeId ?? "all",
      params.cursor ?? "first"
    ),
    queryFn: async () =>
      await cfApi.get<TermSheetListPage>(BASE, {
        params: {
          scope: params.scope,
          aeId: params.aeId,
          cursor: params.cursor,
        },
      }),
    placeholderData: (previous) => previous,
  })
}

export function useTermSheet(dealId: string | undefined) {
  return useQuery<TermSheet>({
    queryKey: termSheetKeys.deal(dealId ?? ""),
    queryFn: async () =>
      await cfApi.get<TermSheet>(`${BASE}/${encodeURIComponent(dealId ?? "")}`),
    enabled: dealId !== undefined && dealId !== "",
    // HubSpot is the source of truth; always refetch when the page opens.
    staleTime: 0,
  })
}

/** Re-prices on every edit, so this is a command rather than a cached read. */
export function usePriceTermSheet(dealId: string) {
  return useMutation<
    PricedTermSheet,
    Error,
    { values: TermSheetValues; version?: string }
  >({
    mutationFn: async (body) =>
      await cfApi.post<PricedTermSheet>(
        `${BASE}/${encodeURIComponent(dealId)}/price`,
        body
      ),
  })
}

const sheetMutation = <TBody, TResult extends TermSheet = TermSheet>(
  dealId: string,
  send: (path: string, body: TBody) => Promise<TResult>
) => {
  const queryClient = useQueryClient()
  return useMutation<TResult, Error, TBody>({
    mutationFn: async (body) =>
      await send(`${BASE}/${encodeURIComponent(dealId)}`, body),
    onSuccess: (sheet) => {
      queryClient.setQueryData(termSheetKeys.deal(dealId), sheet)
    },
  })
}

/** Starts the deal's term sheet on a policy family and version. */
export function useCreateDraft(dealId: string) {
  return sheetMutation<{ family: string; version: string }>(
    dealId,
    async (path, body) => await cfApi.post<TermSheet>(`${path}/draft`, body)
  )
}

/** Merges edited fields into the draft. */
export function useEditDraft(dealId: string) {
  return sheetMutation<TermSheetValues>(
    dealId,
    async (path, changes) =>
      await cfApi.patch<TermSheet>(`${path}/draft`, { changes })
  )
}

export function useChangeDraftVersion(dealId: string) {
  return sheetMutation<string>(
    dealId,
    async (path, version) =>
      await cfApi.put<TermSheet>(`${path}/draft/version`, { version })
  )
}

/** Takes HubSpot's values for `keys` (default: the incoming updates). */
export function usePullFromHubSpot(dealId: string) {
  return sheetMutation<string[] | undefined>(
    dealId,
    async (path, keys) => await cfApi.post<TermSheet>(`${path}/pull`, { keys })
  )
}

/** Writes draft values for `keys` (default: unpushed edits) to HubSpot. */
export function usePushToHubSpot(dealId: string) {
  return sheetMutation<string[] | undefined, PushedTermSheet>(
    dealId,
    async (path, keys) =>
      await cfApi.post<PushedTermSheet>(`${path}/push`, { keys })
  )
}

export function useTermSheetHistory(dealId: string | undefined) {
  return useQuery<{ results: IssuedTermSheetSummary[] }>({
    queryKey: termSheetKeys.history(dealId ?? ""),
    queryFn: async () =>
      await cfApi.get<{ results: IssuedTermSheetSummary[] }>(
        `${BASE}/${encodeURIComponent(dealId ?? "")}/history`
      ),
    enabled: dealId !== undefined && dealId !== "",
  })
}

/** Records the draft (priced server-side on its pinned version). */
export function useIssueTermSheet(dealId: string) {
  const queryClient = useQueryClient()
  return useMutation<IssuedTermSheet, Error, { reason?: string }>({
    mutationFn: async (body) =>
      await cfApi.post<IssuedTermSheet>(
        `${BASE}/${encodeURIComponent(dealId)}/issue`,
        body
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: termSheetKeys.deal(dealId),
      })
    },
  })
}

/** Records a product selection as a new issued term sheet. */
export function useSelectTermSheetProduct(dealId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    IssuedTermSheet,
    Error,
    { id: number; productId: string; reason?: string }
  >({
    mutationFn: async ({ id, ...body }) =>
      await cfApi.post<IssuedTermSheet>(
        `${BASE}/records/${String(id)}/select-product`,
        body
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: termSheetKeys.history(dealId),
      })
    },
  })
}

export function useIssuedTermSheet(id: number | undefined) {
  return useQuery<IssuedTermSheet>({
    queryKey: [...termSheetKeys.all, "record", id ?? 0],
    queryFn: async () =>
      await cfApi.get<IssuedTermSheet>(`${BASE}/records/${String(id ?? 0)}`),
    enabled: id !== undefined,
    staleTime: Infinity,
  })
}
